import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Progress } from "./components/ui/progress";
import { Badge } from "./components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "./components/ui/drawer";
import { Shell, StatCard, Section } from "./components/AtlasUI";
import { seedIfEmpty, getDeals, getStages, moveDealStage, getPortfolio, getLPs } from "./mock";
import { chatCompletion } from "./services/ai";
import KnowledgeBank from "./pages/KnowledgeBank";
import Platform from "./pages/Platform";
import DealDetails from "./pages/DealDetails";
import Identification from "./pages/Identification";
import DueDiligence from "./pages/DueDiligence";
import PortfolioOps from "./pages/PortfolioOps";
import { lpReasons, initUpload, uploadChunk, completeUpload, askDocs } from "./services/knowledge";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Book, BadgeDollarSign, Workflow, Building2, Users, LineChart, Upload, Linkedin, MessageCircle, FileText as FileTextIcon } from "lucide-react";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";

// Seed localStorage synchronously before any component renders
seedIfEmpty();

// ─── Hook to listen for team changes ──────────────────────────────────────────
function useTeamChange() {
  const [teamKey, setTeamKey] = useState(0);
  useEffect(() => {
    const handler = () => setTeamKey(k => k + 1);
    window.addEventListener("atlas_team_changed", handler);
    return () => window.removeEventListener("atlas_team_changed", handler);
  }, []);
  return teamKey;
}

// ─── Shared light-theme card style ───────────────────────────────────────────
const CARD = "bg-white border border-gray-200 shadow-sm";
const CARD_INNER = "bg-gray-50 border border-gray-200";
const LABEL = "text-xs font-medium text-[#616161] uppercase tracking-wide mb-1";
const TEXT = "text-[#383838]";
const MUTED = "text-[#616161]";
const INPUT_CLS = "bg-white border-gray-200 text-[#383838] placeholder:text-[#616161] focus:border-[#0078C1]";
const BTN_PRIMARY = "bg-[#0078C1] hover:bg-[#005A91] text-white";
const BTN_GHOST = "bg-gray-100 hover:bg-gray-200 text-[#383838] border border-gray-200";
const BTN_OUTLINE = "bg-white hover:bg-gray-50 text-[#383838] border border-gray-200";

// ─── Guard ────────────────────────────────────────────────────────────────────
function RedirectGuard({ children }) {
  return children;
}

// ─── Market Signal Helpers ────────────────────────────────────────────────────
const CATEGORIES = ["Macro", "Sector", "Company", "Regulatory"];
const SENTIMENTS = ["Positive", "Neutral", "Negative"];
const REGIONS = ["GCC", "MENA", "South Asia", "North America"];
const SOURCES = ["News", "Filing", "Analyst"];
const SECTORS = ["Healthcare", "Food & Beverage", "Consumer", "EdTech", "AgriTech", "Mobility", "Education", "Food Processing", "Logistics"];

function randomPick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function shuffle(arr) { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
function sample(arr, n) { return shuffle(arr).slice(0, n); }

function makeSignals(n = 6) {
  const texts = [
    "KSA Vision 2030 accelerates healthcare privatisation",
    "UAE F&B sector growing 8% YoY — consolidation opportunity",
    "India D2C brands attracting global PE at premium multiples",
    "MENA EdTech adoption surging post-COVID",
    "GCC logistics infrastructure investment surging",
    "Private credit terms loosening for quality GCC sponsors",
    "Dental chains in KSA seeing 15%+ EBITDA expansion",
    "Vertical farming economics improving on energy cost reduction",
  ];
  return Array.from({ length: n }).map(() => ({
    category: randomPick(CATEGORIES),
    sentiment: randomPick(SENTIMENTS),
    region: randomPick(REGIONS),
    sector: randomPick(SECTORS),
    source: randomPick(SOURCES),
    text: randomPick(texts),
  }));
}

// ─── Overview ─────────────────────────────────────────────────────────────────
function Overview() {
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const nav = useNavigate();
  const closingSoon = useMemo(() => deals.filter(d => d.stage === "LOI" || d.stage === "Closed").slice(0, 5), [deals]);
  const actions = useMemo(() => ([
    { label: "Identification", desc: "Research targets and competitors.", to: "/identification", icon: <Sparkles size={16} className="text-[#0078C1]" /> },
    { label: "Execution", desc: "LBO / DCF models and deal case.", to: "/execution", icon: <BadgeDollarSign size={16} className="text-emerald-600" /> },
    { label: "Pipeline CRM", desc: "Move deals across stages.", to: "/pipeline", icon: <Workflow size={16} className="text-purple-600" /> },
    { label: "Portfolio Dashboard", desc: "KPIs and exit readiness.", to: "/portfolio", icon: <Building2 size={16} className="text-amber-600" /> },
    { label: "Knowledge Bank", desc: "Upload docs and ask questions.", to: "/knowledge", icon: <Book size={16} className="text-sky-600" /> },
  ]), []);
  const quick = useMemo(() => sample(actions, 5), [actions]);

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Active Deals" value={deals.length} />
        <StatCard title="Avg Score" value={Math.round(deals.reduce((a, d) => a + d.score, 0) / Math.max(1, deals.length))} />
        <StatCard title="Pipeline (Sourced)" value={deals.filter(d => d.stage === "Sourced").length} />
        <button className="text-left" onClick={() => nav(`/pipeline`)}>
          <StatCard title="Closed" value={closingSoon.length} sub={closingSoon.map(d => d.name).slice(0, 2).join(", ")} />
        </button>
      </div>
      <Section title="Quick Actions">
        <Card className={CARD}>
          <CardContent className="p-4 space-y-3">
            {quick.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="mt-0.5">{a.icon}</div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${TEXT}`}>{a.label}</div>
                  <div className={`text-xs ${MUTED}`}>{a.desc}</div>
                </div>
                <Button className={`h-8 text-sm ${BTN_PRIMARY}`} onClick={() => nav(a.to)}>Open</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}

// ─── Deal Origination ─────────────────────────────────────────────────────────
function Origination() {
  const [explainMap, setExplainMap] = useState({});
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals().slice(0, 15).sort((a, b) => b.score - a.score), [teamKey]);
  const nav = useNavigate();

  const S_KEY = "atlas_sourcing_campaigns";
  const [campaigns, setCampaigns] = useState(() => {
    try { return JSON.parse(localStorage.getItem(S_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(S_KEY, JSON.stringify(campaigns)); }, [campaigns]);
  const [form, setForm] = useState({ name: "", sector: "", audience: "PE-backed CEOs", channel: "Email", volume: 50, goal: "Intro calls" });
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);

  const createCampaign = () => {
    if (!form.name.trim()) return;
    const c = { id: crypto.randomUUID(), ...form, status: "Idle", createdAt: Date.now(), lastTouch: null, touches: 0 };
    setCampaigns(prev => [c, ...prev]);
    setForm({ name: "", sector: form.sector, audience: form.audience, channel: form.channel, volume: form.volume, goal: form.goal });
  };
  const toggleStatus = (id) => setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: c.status === "Running" ? "Paused" : "Running" } : c));
  const logTouch = (id) => setCampaigns(prev => prev.map(c => c.id === id ? { ...c, touches: (c.touches || 0) + 1, lastTouch: Date.now() } : c));
  const draftOutreach = async (c) => {
    setActiveCampaign(c); setDraftOpen(true); setDraftBusy(true); setDraftText("");
    try {
      const prompt = `Draft a cold outbound sequence for GII. Channel: ${c.channel}. Campaign: ${c.name}. Sector: ${c.sector || "General"}. Audience: ${c.audience}. Goal: ${c.goal}. Provide: 1) Subject line, 2) 4-sentence email, 3) 200-char LinkedIn DM, 4) 3 follow-up bullets. Tone: professional, concise, value-driven.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE origination specialist at GII. Keep copy tight, specific, and compliant." });
      setDraftText(content || "");
    } catch (e) { setDraftText(`Error: ${e.message}`); }
    finally { setDraftBusy(false); }
  };

  const explainScore = async (d) => {
    setExplainMap(prev => ({ ...prev, [d.id]: { loading: true, text: prev[d.id]?.text || "" } }));
    try {
      const { content } = await chatCompletion(
        `Explain score ${d.score} for ${d.name} (sector ${d.sector}, EV ${d.enterpriseValue}M, stage ${d.stage}, geography ${d.geography || "MEA"}). 3 bullets + 1 risk.`,
        { systemPrompt: "You are an M&A analyst at GII. Be specific and concise." }
      );
      setExplainMap(prev => ({ ...prev, [d.id]: { loading: false, text: content } }));
    } catch (e) { setExplainMap(prev => ({ ...prev, [d.id]: { loading: false, text: `Error: ${e.message}` } })); }
  };

  const scoreColor = (s) => s >= 85 ? "bg-emerald-100 text-emerald-700" : s >= 70 ? "bg-blue-100 text-[#0078C1]" : "bg-gray-100 text-[#616161]";

  return (
    <div className="space-y-6">
      <Section title="Predictive Deal Scoring">
        <Card className={CARD}>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>Company</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>Sector</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>Geography</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>Stage</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>Score</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>EV ($M)</th>
                    <th className={`text-left px-4 py-3 font-semibold ${TEXT} text-xs uppercase tracking-wide`}>EBITDA %</th>
                    <th className={`px-4 py-3`}></th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map(d => (
                    <React.Fragment key={d.id}>
                      <tr className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <button onClick={() => nav(`/deal/${d.id}`)} className={`font-semibold ${TEXT} hover:text-[#0078C1] hover:underline text-left`}>{d.name}</button>
                          {d.description && <div className={`text-xs ${MUTED} mt-0.5 max-w-xs truncate`}>{d.description}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-[#0078C1]">{d.sector}</span>
                        </td>
                        <td className={`px-4 py-3 text-sm ${MUTED}`}>{d.geography || "MEA"}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-[#616161]">{d.stage}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-100 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full bg-[#0078C1]" style={{ width: `${d.score}%` }} />
                            </div>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreColor(d.score)}`}>{d.score}</span>
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${TEXT}`}>{d.enterpriseValue}</td>
                        <td className={`px-4 py-3 text-sm ${MUTED}`}>{d.ebitdaMargin}%</td>
                        <td className="px-4 py-3">
                          <Button className={`h-7 text-xs ${BTN_OUTLINE}`} onClick={() => explainScore(d)}>
                            {explainMap[d.id]?.loading ? "…" : "Explain"}
                          </Button>
                        </td>
                      </tr>
                      {explainMap[d.id]?.text && (
                        <tr className="bg-blue-50">
                          <td colSpan={8} className="px-4 py-3">
                            <div className={`text-xs ${TEXT} whitespace-pre-wrap leading-relaxed`}>{explainMap[d.id].text}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </Section>

      <Section title="Deal Sourcing" action={<span className={`text-xs ${MUTED}`}>Outbound campaigns (email / LinkedIn)</span>}>
        <Card className={CARD}>
          <CardContent className="p-4 space-y-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <div className={LABEL}>Campaign Name</div>
                <Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} className={INPUT_CLS} placeholder="e.g., KSA Healthcare Rollup" />
              </div>
              <div>
                <div className={LABEL}>Sector</div>
                <Input value={form.sector} onChange={e => setForm(prev => ({ ...prev, sector: e.target.value }))} className={INPUT_CLS} placeholder="e.g., Healthcare" />
              </div>
              <div>
                <div className={LABEL}>Audience</div>
                <Input value={form.audience} onChange={e => setForm(prev => ({ ...prev, audience: e.target.value }))} className={INPUT_CLS} placeholder="e.g., Founder-CEOs" />
              </div>
              <div>
                <div className={LABEL}>Channel</div>
                <select value={form.channel} onChange={e => setForm(prev => ({ ...prev, channel: e.target.value }))} className={`w-full border border-gray-200 rounded-md text-sm px-3 py-2 bg-white text-[#383838] focus:outline-none focus:border-[#0078C1]`}>
                  <option>Email</option>
                  <option>LinkedIn</option>
                </select>
              </div>
              <div>
                <div className={LABEL}>Weekly Volume</div>
                <Input type="number" value={form.volume} onChange={e => setForm(prev => ({ ...prev, volume: Number(e.target.value) }))} className={INPUT_CLS} />
              </div>
              <div className="lg:col-span-2">
                <div className={LABEL}>Goal</div>
                <Input value={form.goal} onChange={e => setForm(prev => ({ ...prev, goal: e.target.value }))} className={INPUT_CLS} placeholder="e.g., 10 intro calls / week" />
              </div>
              <div className="flex items-end">
                <Button className={`h-9 w-full ${BTN_PRIMARY}`} onClick={createCampaign} disabled={!form.name.trim()}>Create Campaign</Button>
              </div>
            </div>
            {campaigns.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {["Name", "Sector", "Audience", "Channel", "Vol/wk", "Status", "Touches", "Actions"].map(h => (
                        <th key={h} className={`text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide ${TEXT}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className={`px-3 py-2 font-medium ${TEXT}`}>{c.name}</td>
                        <td className={`px-3 py-2 ${MUTED}`}>{c.sector || "—"}</td>
                        <td className={`px-3 py-2 ${MUTED}`}>{c.audience}</td>
                        <td className={`px-3 py-2 ${MUTED}`}>{c.channel}</td>
                        <td className={`px-3 py-2 ${MUTED}`}>{c.volume}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === "Running" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-[#616161]"}`}>{c.status}</span>
                        </td>
                        <td className={`px-3 py-2 ${MUTED}`}>{c.touches || 0}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Button className={`h-7 text-xs ${BTN_GHOST}`} onClick={() => toggleStatus(c.id)}>{c.status === "Running" ? "Pause" : "Run"}</Button>
                            <Button className={`h-7 text-xs ${BTN_GHOST}`} onClick={() => logTouch(c.id)}>+ Touch</Button>
                            <Button className={`h-7 text-xs ${BTN_PRIMARY}`} onClick={() => draftOutreach(c)}>Draft</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {campaigns.length === 0 && (
              <div className={`text-sm ${MUTED} py-4 text-center`}>No campaigns yet. Create one above.</div>
            )}
          </CardContent>
        </Card>

        <Dialog open={draftOpen} onOpenChange={setDraftOpen}>
          <DialogContent className="bg-white text-[#383838] border-gray-200 max-w-2xl">
            <DialogHeader><DialogTitle className={TEXT}>Outbound Draft • {activeCampaign?.name || ""}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <div className={`text-xs ${MUTED}`}>Channel: {activeCampaign?.channel} • Audience: {activeCampaign?.audience}</div>
              <Card className={CARD_INNER}><CardContent className={`p-4 text-sm whitespace-pre-wrap min-h-40 ${TEXT}`}>{draftBusy ? "Generating…" : (draftText || "Click Draft to generate copy.")}</CardContent></Card>
            </div>
          </DialogContent>
        </Dialog>
      </Section>
    </div>
  );
}

// ─── Execution (LBO + DCF) ────────────────────────────────────────────────────
function Execution() {
  const [lbo, setLbo] = useState({ entryEV: 500, ebitda: 50, debtPct: 60, interest: 8, entryMultiple: 10, exitMultiple: 11, years: 5, taxRate: 25, fcfPct: 60 });
  const [lboRes, setLboRes] = useState({ irr: 0, moic: 0, exitEV: 0, exitEquity: 0, debtRemaining: 0 });
  const [dcf, setDcf] = useState({ revenue: 300, ebitdaMargin: 25, taxRate: 25, capexPct: 5, nwcPct: 2, wacc: 10, growth: 8, terminalGrowth: 3, years: 5 });
  const [dcfRes, setDcfRes] = useState({ ev: 0 });
  const [busy, setBusy] = useState(false);
  const [talk, setTalk] = useState("");
  const [dealCase, setDealCase] = useState("");
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const [chosen, setChosen] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filteredDeals = useMemo(() => {
    const q = search.toLowerCase();
    return deals.filter(d => !q || d.name.toLowerCase().includes(q) || d.sector.toLowerCase().includes(q));
  }, [deals, search]);

  // LBO calc
  useEffect(() => {
    const entryEquity = lbo.entryEV * (1 - lbo.debtPct / 100);
    const debt = lbo.entryEV * (lbo.debtPct / 100);
    const annualInterest = debt * (lbo.interest / 100);
    const annualFCF = lbo.ebitda * (lbo.fcfPct / 100) * (1 - lbo.taxRate / 100) - annualInterest;
    const debtRemaining = Math.max(0, debt - annualFCF * lbo.years);
    const exitEV = lbo.ebitda * lbo.exitMultiple;
    const exitEquity = Math.max(0, exitEV - debtRemaining);
    const moic = entryEquity > 0 ? +(exitEquity / entryEquity).toFixed(2) : 0;
    const irr = entryEquity > 0 ? +((Math.pow(moic, 1 / lbo.years) - 1) * 100).toFixed(1) : 0;
    setLboRes({ irr, moic, exitEV: Math.round(exitEV), exitEquity: Math.round(exitEquity), debtRemaining: Math.round(debtRemaining) });
  }, [lbo]);

  // DCF calc
  useEffect(() => {
    let pv = 0;
    let rev = dcf.revenue;
    for (let y = 1; y <= dcf.years; y++) {
      rev *= (1 + dcf.growth / 100);
      const fcf = rev * (dcf.ebitdaMargin / 100) * (1 - dcf.taxRate / 100) - rev * (dcf.capexPct / 100) - rev * (dcf.nwcPct / 100);
      pv += fcf / Math.pow(1 + dcf.wacc / 100, y);
    }
    const terminalFCF = rev * (dcf.ebitdaMargin / 100) * (1 - dcf.taxRate / 100) * (1 + dcf.terminalGrowth / 100);
    const terminalValue = terminalFCF / Math.max(0.001, (dcf.wacc - dcf.terminalGrowth) / 100);
    const pvTerminal = terminalValue / Math.pow(1 + dcf.wacc / 100, dcf.years);
    setDcfRes({ ev: Math.round(pv + pvTerminal) });
  }, [dcf]);

  const buildCase = async () => {
    setBusy(true); setTalk(""); setDealCase("");
    try {
      const ctx = chosen ? `Company: ${chosen.name}, Sector: ${chosen.sector}, EV: $${chosen.enterpriseValue}M, Score: ${chosen.score}` : "No specific deal selected";
      const prompt = `${ctx}\nLBO Results: IRR ${lboRes.irr}%, MOIC ${lboRes.moic}x, Exit EV $${lboRes.exitEV}M\nDCF EV: $${dcfRes.ev}M\nGenerate: 1) 3 IC talking points, 2) Deal case summary (3-4 sentences), 3) Key risks and mitigants.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE deal professional at GII. Be concise, structured, and investment-committee ready." });
      const parts = (content || "").split(/\n\n/);
      setTalk(parts[0] || content);
      setDealCase(parts.slice(1).join("\n\n") || "");
    } catch (e) { setTalk(`Error: ${e.message}`); }
    setBusy(false);
  };

  function NumInput({ label, suffix, value, onChange, step = 1 }) {
    return (
      <div>
        <div className={LABEL}>{label}{suffix && <span className="ml-1 normal-case text-[#616161]">({suffix})</span>}</div>
        <div className="flex items-center border border-gray-200 rounded-md overflow-hidden bg-white">
          <input
            type="number"
            value={value}
            step={step}
            onChange={e => onChange(Number(e.target.value))}
            className={`flex-1 px-3 py-2 text-sm ${TEXT} bg-transparent outline-none`}
          />
          {suffix && <span className={`px-2 text-xs ${MUTED} bg-gray-50 border-l border-gray-200 py-2`}>{suffix}</span>}
        </div>
      </div>
    );
  }

  function ResultCard({ label, value }) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className={`text-xs ${MUTED} mb-1`}>{label}</div>
        <div className={`text-lg font-bold ${TEXT}`}>{value}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Deal Picker */}
      <Section title="Choose Deal">
        <Card className={CARD}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                {chosen ? (
                  <div className="flex items-center gap-3">
                    <span className={`font-semibold ${TEXT}`}>{chosen.name}</span>
                    <span className={`text-xs ${MUTED}`}>{chosen.sector}</span>
                    <span className={`text-xs ${MUTED}`}>EV: ${chosen.enterpriseValue}M</span>
                    <span className={`text-xs ${MUTED}`}>Score: {chosen.score}</span>
                  </div>
                ) : (
                  <span className={`text-sm ${MUTED}`}>No deal selected — select one to pre-fill models</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {chosen && <Button className={`h-8 text-sm ${BTN_GHOST}`} onClick={() => setChosen(null)}>Clear</Button>}
                <Button className={`h-8 text-sm ${BTN_OUTLINE}`} onClick={() => setPickerOpen(v => !v)}>{pickerOpen ? "Hide" : "Browse Deals"}</Button>
              </div>
            </div>
            {pickerOpen && (
              <div className="space-y-2 border-t border-gray-100 pt-3">
                <Input placeholder="Search deals…" value={search} onChange={e => setSearch(e.target.value)} className={INPUT_CLS} />
                <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                  {filteredDeals.slice(0, 60).map(d => (
                    <div key={d.id} className="py-2 flex items-center gap-3 hover:bg-gray-50 px-1 rounded">
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${TEXT}`}>{d.name}</div>
                        <div className={`text-xs ${MUTED}`}>{d.sector} • {d.stage} • EV: ${d.enterpriseValue}M</div>
                      </div>
                      <Button className={`h-7 text-xs ${BTN_PRIMARY}`} onClick={() => { setChosen(d); setPickerOpen(false); }}>Select</Button>
                      <Link to={`/deal/${d.id}`} className={`text-xs ${MUTED} underline hover:text-[#0078C1]`}>Open</Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </Section>

      {/* LBO Model */}
      <Section title="Automated LBO Model">
        <Card className={CARD}>
          <CardContent className="p-4 space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <NumInput label="Entry EV" suffix="$M" value={lbo.entryEV} onChange={v => setLbo(p => ({ ...p, entryEV: v }))} />
              <NumInput label="EBITDA" suffix="$M" value={lbo.ebitda} onChange={v => setLbo(p => ({ ...p, ebitda: v }))} />
              <NumInput label="Debt % of EV" suffix="%" value={lbo.debtPct} onChange={v => setLbo(p => ({ ...p, debtPct: v }))} />
              <NumInput label="Interest Rate" suffix="%" value={lbo.interest} onChange={v => setLbo(p => ({ ...p, interest: v }))} step={0.1} />
              <NumInput label="Entry Multiple" suffix="x" value={lbo.entryMultiple} onChange={v => setLbo(p => ({ ...p, entryMultiple: v }))} step={0.1} />
              <NumInput label="Exit Multiple" suffix="x" value={lbo.exitMultiple} onChange={v => setLbo(p => ({ ...p, exitMultiple: v }))} step={0.1} />
              <NumInput label="Hold Period" suffix="yrs" value={lbo.years} onChange={v => setLbo(p => ({ ...p, years: v }))} />
              <NumInput label="Tax Rate" suffix="%" value={lbo.taxRate} onChange={v => setLbo(p => ({ ...p, taxRate: v }))} step={0.1} />
              <NumInput label="FCF % of EBITDA" suffix="%" value={lbo.fcfPct} onChange={v => setLbo(p => ({ ...p, fcfPct: v }))} step={0.1} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-gray-100">
              <ResultCard label="IRR" value={`${lboRes.irr}%`} />
              <ResultCard label="MOIC" value={`${lboRes.moic}x`} />
              <ResultCard label="Exit EV ($M)" value={lboRes.exitEV} />
              <ResultCard label="Exit Equity ($M)" value={lboRes.exitEquity} />
              <ResultCard label="Debt Remaining ($M)" value={lboRes.debtRemaining} />
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* DCF Model */}
      <Section title="Automated DCF Model">
        <Card className={CARD}>
          <CardContent className="p-4 space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <NumInput label="Revenue" suffix="$M" value={dcf.revenue} onChange={v => setDcf(p => ({ ...p, revenue: v }))} />
              <NumInput label="EBITDA Margin" suffix="%" value={dcf.ebitdaMargin} onChange={v => setDcf(p => ({ ...p, ebitdaMargin: v }))} step={0.1} />
              <NumInput label="Tax Rate" suffix="%" value={dcf.taxRate} onChange={v => setDcf(p => ({ ...p, taxRate: v }))} step={0.1} />
              <NumInput label="Capex % of Rev" suffix="%" value={dcf.capexPct} onChange={v => setDcf(p => ({ ...p, capexPct: v }))} step={0.1} />
              <NumInput label="NWC % of Rev" suffix="%" value={dcf.nwcPct} onChange={v => setDcf(p => ({ ...p, nwcPct: v }))} step={0.1} />
              <NumInput label="WACC" suffix="%" value={dcf.wacc} onChange={v => setDcf(p => ({ ...p, wacc: v }))} step={0.1} />
              <NumInput label="Revenue CAGR" suffix="%" value={dcf.growth} onChange={v => setDcf(p => ({ ...p, growth: v }))} step={0.1} />
              <NumInput label="Terminal Growth" suffix="%" value={dcf.terminalGrowth} onChange={v => setDcf(p => ({ ...p, terminalGrowth: v }))} step={0.1} />
            </div>
            <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-gray-100">
              <ResultCard label="Enterprise Value ($M)" value={dcfRes.ev} />
              <ResultCard label="LBO IRR" value={`${lboRes.irr}%`} />
              <ResultCard label="LBO / DCF Spread ($M)" value={Math.abs(lboRes.exitEV - dcfRes.ev)} />
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Atlas Insight */}
      <Section title="Atlas Insight" action={
        <Button className={`${BTN_PRIMARY} h-9`} onClick={buildCase} disabled={busy}>
          {busy ? "Generating…" : "Build IC Case"}
        </Button>
      }>
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className={CARD}>
            <CardContent className="p-4">
              <div className={`text-xs font-semibold uppercase tracking-wide ${MUTED} mb-2`}>IC Talking Points</div>
              <div className={`text-sm whitespace-pre-wrap min-h-32 ${TEXT}`}>{talk || "Click Build IC Case to generate talking points."}</div>
            </CardContent>
          </Card>
          <Card className={CARD}>
            <CardContent className="p-4">
              <div className={`text-xs font-semibold uppercase tracking-wide ${MUTED} mb-2`}>Deal Case</div>
              <div className={`text-sm whitespace-pre-wrap min-h-32 ${TEXT}`}>{dealCase || "Deal case will use LBO and DCF results to support the investment thesis."}</div>
            </CardContent>
          </Card>
        </div>
      </Section>
    </div>
  );
}

// ─── Pipeline CRM ─────────────────────────────────────────────────────────────
function Pipeline() {
  const nav = useNavigate();
  const location = useLocation();
  const stages = getStages();
  const teamKey = useTeamChange();
  const initialDeals = useMemo(() => getDeals(), [teamKey]);
  const [allDeals, setAllDeals] = useState(initialDeals);
  
  // Reset allDeals when team changes
  useEffect(() => {
    setAllDeals(initialDeals);
  }, [initialDeals]);
  
  const allSectors = useMemo(() => Array.from(new Set(initialDeals.map(d => d.sector))), [initialDeals]);
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("atlas_pipeline_filters");
    if (saved) try {
      const parsed = JSON.parse(saved);
      // Validate cached sectors against actual deal sectors — discard stale old data
      const validSectors = Array.from(new Set(initialDeals.map(d => d.sector)));
      const cachedSectors = (parsed.sectors || []).filter(s => validSectors.includes(s));
      return { ...parsed, sectors: cachedSectors.length > 0 ? cachedSectors : validSectors };
    } catch { }
    return { search: "", sectors: allSectors, stages };
  });
  
  // Reset filters when team changes
  useEffect(() => {
    setFilters({ search: "", sectors: allSectors, stages });
  }, [teamKey, allSectors, stages]);
  
  useEffect(() => { localStorage.setItem("atlas_pipeline_filters", JSON.stringify(filters)); }, [filters]);

  const params = new URLSearchParams(location.search);
  const focusId = params.get("focus");
  const [open, setOpen] = useState(!!focusId);
  const [activeId, setActiveId] = useState(focusId || "");
  useEffect(() => { if (focusId) { setActiveId(focusId); setOpen(true); } }, [focusId]);

  const filtered = useMemo(() => {
    const q = (filters.search || "").toLowerCase();
    return allDeals.filter(d =>
      (!q || d.name.toLowerCase().includes(q)) &&
      (filters.sectors?.includes(d.sector)) &&
      (filters.stages?.includes(d.stage))
    );
  }, [allDeals, filters]);

  const columns = useMemo(() => {
    const map = Object.fromEntries(stages.map(s => [s, []]));
    for (const d of filtered) map[d.stage]?.push(d);
    return map;
  }, [filtered, stages]);

  const onDragStart = (e, deal) => { e.dataTransfer.setData("application/json", JSON.stringify({ id: deal.id })); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, stage) => {
    e.preventDefault();
    try {
      const { id } = JSON.parse(e.dataTransfer.getData("application/json"));
      setAllDeals(prev => prev.map(d => d.id === id ? { ...d, stage } : d));
      try { moveDealStage(id, stage); } catch (_) { }
    } catch (_) { }
  };

  const activeDeal = allDeals.find(d => d.id === activeId);
  const changeStage = (id, stage) => {
    setAllDeals(prev => prev.map(d => d.id === id ? { ...d, stage } : d));
    try { moveDealStage(id, stage); } catch (_) { }
  };

  const stageColors = { "Sourced": "border-gray-200", "Contacted": "border-blue-200", "Evaluating": "border-yellow-200", "IC Ready": "border-purple-200", "LOI": "border-orange-200", "Closed": "border-emerald-200" };

  return (
    <div className="space-y-4">
      <Card className={CARD}>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input placeholder="Search deals…" value={filters.search || ""} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} className={`${INPUT_CLS} w-48 h-8 text-sm`} />
            <div className="flex flex-wrap gap-1">
              {allSectors.map(s => (
                <button key={s} onClick={() => setFilters(p => ({ ...p, sectors: p.sectors.includes(s) ? p.sectors.filter(x => x !== s) : [...p.sectors, s] }))}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${filters.sectors?.includes(s) ? "bg-[#0078C1] text-white border-[#0078C1]" : "bg-white text-[#616161] border-gray-200 hover:border-[#0078C1]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {stages.map(stage => (
          <div key={stage} className={`flex-shrink-0 w-52 border-t-2 ${stageColors[stage] || "border-gray-200"}`}
            onDragOver={onDragOver} onDrop={e => onDrop(e, stage)}>
            <div className="flex items-center justify-between mb-2 pt-2">
              <span className={`text-xs font-semibold uppercase tracking-wide ${TEXT}`}>{stage}</span>
              <span className={`text-xs ${MUTED} bg-gray-100 px-1.5 py-0.5 rounded-full`}>{columns[stage]?.length || 0}</span>
            </div>
            <div className="space-y-2 min-h-24">
              {(columns[stage] || []).map(d => (
                <div key={d.id} draggable onDragStart={e => onDragStart(e, d)} onClick={() => { setActiveId(d.id); setOpen(true); }}
                  className={`bg-white border border-gray-200 rounded-lg p-2.5 cursor-move hover:border-[#0078C1] hover:shadow-sm transition-all ${activeId === d.id ? "ring-1 ring-[#0078C1]" : ""}`}>
                  <div className={`font-medium text-sm truncate ${TEXT}`}>{d.name}</div>
                  <div className={`text-xs ${MUTED} mt-0.5`}>{d.sector}</div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="flex-1 bg-gray-100 rounded-full h-1">
                      <div className="h-1 rounded-full bg-[#0078C1]" style={{ width: `${d.score}%` }} />
                    </div>
                    <span className={`text-xs font-medium ${TEXT}`}>{d.score}</span>
                    <span className={`text-xs ${MUTED}`}>${d.enterpriseValue}M</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="bg-white text-[#383838] border-gray-200 max-h-[90vh]">
          <div className="p-4 border-b border-gray-100">
            <DrawerTitle className={TEXT}>Deal Details</DrawerTitle>
          </div>
          <div className="px-4 pb-4 space-y-3 overflow-y-auto">
            {!activeDeal && <div className={`${MUTED} text-sm`}>No deal selected.</div>}
            {activeDeal && (
              <>
                <div className="flex items-center justify-between pt-2">
                  <div className={`text-lg font-bold ${TEXT}`}>{activeDeal.name}</div>
                  <Button className={`h-8 text-sm ${BTN_PRIMARY}`} onClick={() => nav(`/deal/${activeDeal.id}`)}>Open Deal</Button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[["Sector", activeDeal.sector], ["Stage", activeDeal.stage], ["EV ($M)", activeDeal.enterpriseValue], ["Score", activeDeal.score]].map(([l, v]) => (
                    <div key={l} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className={`text-xs ${MUTED} mb-1`}>{l}</div>
                      <div className={`font-semibold ${TEXT}`}>{v}</div>
                    </div>
                  ))}
                </div>
                {activeDeal.description && <div className={`text-sm ${MUTED}`}>{activeDeal.description}</div>}
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${TEXT}`}>Change Stage:</span>
                  <select value={activeDeal.stage} onChange={e => changeStage(activeDeal.id, e.target.value)}
                    className={`border border-gray-200 rounded-md text-sm px-2 py-1.5 bg-white ${TEXT} focus:outline-none focus:border-[#0078C1]`}>
                    {stages.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

// ─── Portfolio ─────────────────────────────────────────────────────────────────
function Portfolio() {
  const teamKey = useTeamChange();
  const pf = useMemo(() => getPortfolio(), [teamKey]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [strategy, setStrategy] = useState("");

  const openStrategy = async (p) => {
    setSel(p); setOpen(true); setBusy(true); setStrategy("");
    try {
      const message = `Portfolio company: ${p.name}\nSector: ${p.sector || "N/A"}\nCountry: ${p.country || "N/A"}\nIRR: ${p.irr}%\nMOIC: ${p.moic}x\nRevenue: $${p.revenue}M\nEBITDA: $${p.ebitda}M\nValue Drivers: ${(p.valueDrivers || []).join(", ")}\nExit Readiness: ${p.exitReadiness}%\n\nRecommend: 1) Best exit path with rationale, 2) Timeline and milestones, 3) Value-maximizing levers in next 6-12 months.`;
      const { content } = await chatCompletion(message, { systemPrompt: "You are a seasoned PE exit advisor at GII. Be concise, structured, and action-oriented." });
      setStrategy(content || "");
    } catch (e) { setStrategy(`Error: ${e.message}`); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Portfolio Companies">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pf.map(p => (
            <Card key={p.id} onClick={() => openStrategy(p)} className={`${CARD} cursor-pointer hover:border-[#0078C1] hover:shadow-md transition-all`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className={`font-bold text-sm ${TEXT}`}>{p.name}</div>
                    <div className={`text-xs ${MUTED} mt-0.5`}>{p.sector} • {p.country}</div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{p.moic}x</span>
                </div>
                <div className={`text-xs ${MUTED} mb-2`}>IRR {p.irr}% • Rev ${p.revenue}M • EBITDA ${p.ebitda}M</div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs ${MUTED}`}>Exit Readiness</span>
                    <span className={`text-xs font-semibold ${TEXT}`}>{p.exitReadiness}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-[#0078C1]" style={{ width: `${p.exitReadiness}%` }} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(p.valueDrivers || []).map(v => (
                    <span key={v} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-[#0078C1]">{v}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-[#383838] border-gray-200 max-w-2xl">
          <DialogHeader><DialogTitle className={TEXT}>Exit Strategy • {sel?.name || ""}</DialogTitle></DialogHeader>
          <Card className={CARD_INNER}>
            <CardContent className={`p-4 text-sm whitespace-pre-wrap min-h-40 ${TEXT}`}>
              {busy ? "Generating exit strategy…" : (strategy || "Click a portfolio card to generate strategy.")}
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── LP ────────────────────────────────────────────────────────────────────────
function LP() {
  const stages = getStages();
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals().slice(0, 10), [teamKey]);
  const [sel, setSel] = useState(deals[0]?.id || "");
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const selectedDeal = deals.find(d => d.id === sel);
  
  // Update sel when team changes
  useEffect(() => {
    if (deals.length > 0 && !deals.find(d => d.id === sel)) {
      setSel(deals[0].id);
    }
  }, [deals, sel]);

  const explain = async () => {
    if (!selectedDeal) return; setBusy(true);
    try {
      const res = await lpReasons({ deal_name: selectedDeal.name, stage: selectedDeal.stage, sector: selectedDeal.sector, notes: `EV ${selectedDeal.enterpriseValue} Score ${selectedDeal.score}` });
      setRationale(res.rationale || res.content || "");
    } catch (e) { setRationale(`Error: ${e.message}`); } finally { setBusy(false); }
  };

  const stagePercent = (stage) => { const idx = Math.max(0, stages.indexOf(stage)); return Math.round((idx / Math.max(1, stages.length - 1)) * 100); };

  return (
    <div className="space-y-6">
      <Section title="Deal Status (Progress by Stage)">
        <Card className={CARD}>
          <CardContent className="p-4 space-y-3">
            {deals.map(d => (
              <div key={d.id} className="grid grid-cols-1 md:grid-cols-4 items-center gap-3">
                <div className="md:col-span-1">
                  <div className={`text-sm font-semibold truncate ${TEXT}`}>{d.name}</div>
                  <div className={`text-xs ${MUTED}`}>{d.stage}</div>
                </div>
                <div className="md:col-span-3 flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-[#0078C1]" style={{ width: `${stagePercent(d.stage)}%` }} />
                  </div>
                  <span className={`text-xs font-medium ${TEXT} w-8`}>{stagePercent(d.stage)}%</span>
                  <Button className={`h-7 text-xs ${BTN_GHOST} ml-auto`} onClick={() => nav(`/deal/${d.id}`)}>Open</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </Section>
      <Section title="Explain Status to LP">
        <div className="flex items-center gap-2 mb-3">
          <select value={sel} onChange={e => setSel(e.target.value)} className={`border border-gray-200 rounded-md text-sm px-3 py-2 bg-white ${TEXT} focus:outline-none focus:border-[#0078C1]`}>
            {deals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Button className={`${BTN_PRIMARY} h-9`} onClick={explain}>{busy ? "Thinking…" : "Explain Status"}</Button>
        </div>
        <Card className={CARD}>
          <CardContent className={`p-4 text-sm whitespace-pre-wrap min-h-24 ${TEXT}`}>{rationale || "Select a deal and click Explain Status."}</CardContent>
        </Card>
      </Section>
    </div>
  );
}

// ─── Knowledge ────────────────────────────────────────────────────────────────
function Knowledge() {
  const nav = useNavigate();
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const sectors = useMemo(() => Array.from(new Set(deals.map(d => d.sector))), [deals]);
  const topDealsBySector = useMemo(() => {
    const map = {};
    sectors.forEach(sec => { map[sec] = deals.filter(d => d.sector === sec).sort((a, b) => b.score - a.score).slice(0, 5); });
    return map;
  }, [deals, sectors]);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const handleUpload = async () => {
    if (!file) return; setUploading(true); setUploadMsg("Initializing upload…");
    try {
      const init = await initUpload({ filename: file.name, size: file.size, mime: file.type || "application/octet-stream" });
      const { upload_id } = init;
      const CHUNK = 256 * 1024;
      const total = Math.max(1, Math.ceil(file.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const start = i * CHUNK; const end = Math.min(file.size, start + CHUNK);
        setUploadMsg(`Uploading chunk ${i + 1}/${total}…`);
        await uploadChunk({ uploadId: upload_id, index: i, total, blob: file.slice(start, end) });
      }
      setUploadMsg("Finalizing…"); await completeUpload({ uploadId: upload_id }); setUploadMsg("Upload complete.");
    } catch (e) { setUploadMsg(`Error: ${e.message}`); } finally { setUploading(false); }
  };
  const handleAsk = async () => {
    if (!question.trim()) return; setAsking(true); setAnswer("");
    try { const res = await askDocs(question.trim()); setAnswer(res.answer || res.content || ""); }
    catch (e) { setAnswer(`Error: ${e.message}`); } finally { setAsking(false); }
  };

  return (
    <div className="space-y-6">
      <Section title="Knowledge Bank" action={<span className={`text-xs ${MUTED}`}>Upload docs and ask questions</span>}>
        <Card className={CARD}>
          <CardContent className="p-4 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={LABEL}>Upload a document</div>
                <div className="flex items-center gap-2">
                  <input id="kb-file" type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
                  <Button className={`h-9 ${BTN_OUTLINE}`} onClick={() => document.getElementById("kb-file")?.click()}>
                    <Upload size={14} className="mr-1.5" /> Choose File
                  </Button>
                  {file && <span className={`text-xs ${MUTED} truncate max-w-[180px]`}>{file.name}</span>}
                </div>
                {file && <Button className={`h-9 mt-2 ${BTN_PRIMARY}`} onClick={handleUpload} disabled={uploading}>{uploading ? "Uploading…" : "Upload"}</Button>}
                {uploadMsg && <div className={`text-xs ${MUTED} mt-1`}>{uploadMsg}</div>}
              </div>
              <div>
                <div className={LABEL}>Ask the Knowledge Bank</div>
                <div className="flex items-center gap-2">
                  <Input placeholder="e.g., What are pricing optimization levers?" value={question} onChange={e => setQuestion(e.target.value)} className={INPUT_CLS} />
                  <Button onClick={handleAsk} disabled={asking} className={`h-9 ${BTN_PRIMARY}`}>{asking ? "…" : "Ask"}</Button>
                </div>
                {answer && <div className={`mt-2 text-sm whitespace-pre-wrap bg-blue-50 border border-blue-100 rounded p-3 ${TEXT}`}>{answer}</div>}
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>
      <Section title="Sectors and Top Deals">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.map(sec => (
            <Card key={sec} className={CARD}>
              <CardContent className="p-4">
                <div className={`text-sm font-bold ${TEXT} mb-2`}>{sec}</div>
                <div className="flex flex-wrap gap-1.5">
                  {topDealsBySector[sec].map(d => (
                    <button key={d.id} onClick={() => nav(`/deal/${d.id}`)}
                      className={`text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 hover:bg-[#0078C1] hover:text-white hover:border-[#0078C1] transition-colors ${TEXT}`}>
                      {d.name}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Competition ──────────────────────────────────────────────────────────────
function Competition() {
  const [date] = useState(new Date().toLocaleDateString());
  const [items, setItems] = useState(() => {
    const headlines = [
      { src: "Bloomberg", what: "KSA healthcare privatisation accelerates — 3 new hospital tenders announced", tag: "Sector" },
      { src: "Reuters", what: "UAE F&B consolidation: major chain acquired at 12x EBITDA", tag: "Deal Activity" },
      { src: "FT", what: "India D2C beauty brands attract $500M+ PE inflows in Q1", tag: "Valuation" },
      { src: "WSJ", what: "MENA EdTech funding up 40% YoY on AI curriculum demand", tag: "Sector" },
      { src: "Bloomberg", what: "GCC logistics: $2B warehousing REIT launched in Riyadh", tag: "Financing" },
    ];
    return headlines.map((h, i) => ({ id: `CR${i + 1}`, ...h, implication: "Click Analyze to generate implications." }));
  });
  const [busyId, setBusyId] = useState("");

  const analyze = async (it) => {
    setBusyId(it.id);
    try {
      const prompt = `Headline: ${it.what} (Source: ${it.src}).\nAs a GII investment analyst, list 3-4 direct, actionable implications for our deal sourcing, valuation, and process this week. Keep it tight.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a GII M&A market analyst. Be specific and concise." });
      setItems(prev => prev.map(x => x.id === it.id ? { ...x, implication: content } : x));
    } catch (e) { setItems(prev => prev.map(x => x.id === it.id ? { ...x, implication: `Error: ${e.message}` } : x)); }
    finally { setBusyId(""); }
  };

  return (
    <div className="space-y-4">
      <Section title="Competition Research" action={<span className={`text-xs ${MUTED}`}>Daily market/competitor actions • {date}</span>}>
        <div className="space-y-3">
          {items.map(it => (
            <Card key={it.id} className={CARD}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className={`text-sm font-semibold ${TEXT}`}>{it.what}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs ${MUTED}`}>{it.src}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#0078C1] font-medium">{it.tag}</span>
                    </div>
                  </div>
                  <Button className={`h-8 text-sm ${BTN_PRIMARY} shrink-0`} onClick={() => analyze(it)} disabled={busyId === it.id}>
                    {busyId === it.id ? "Analyzing…" : "Analyze"}
                  </Button>
                </div>
                {it.implication !== "Click Analyze to generate implications." && (
                  <div className={`mt-3 text-sm whitespace-pre-wrap bg-blue-50 border border-blue-100 rounded p-3 ${TEXT}`}>{it.implication}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Routes ───────────────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Overview />} />
      <Route path="/pipeline" element={<Pipeline />} />
      <Route path="/knowledge" element={<Knowledge />} />
      <Route path="/portfolio" element={<PortfolioOps />} />
      <Route path="/lp" element={<LP />} />
      <Route path="/knowledge-bank" element={<KnowledgeBank />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/deal/:id" element={<DealDetails />} />
      <Route path="/opportunities" element={<Identification />} />
      <Route path="/due-diligence" element={<DueDiligence />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { seedIfEmpty(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Shell>
          <ErrorBoundary>
            <RedirectGuard>
              <AppRoutes />
            </RedirectGuard>
          </ErrorBoundary>
        </Shell>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
